import { useState } from "react";
import type { ImportJobApiItem, ImportJobStatus } from "@recipevault/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui/card.js";
import { Progress } from "@/ui/progress.js";
import { photosObjectUrl } from "@/features/recipes/photoUrl.js";
import { CoverPlaceholder } from "@/features/recipes/CoverPlaceholder.js";

const STATUS_ORDER: ImportJobStatus[] = [
  "queued",
  "fetching",
  "extracting",
  "saving",
  "done",
];

const STATUS_LABEL: Record<ImportJobStatus, string> = {
  queued: "Queued",
  fetching: "Fetching",
  extracting: "Extracting",
  saving: "Saving",
  done: "Done",
  failed: "Failed",
  over_cap: "Monthly cap reached",
};

function progressValue(status: ImportJobStatus): number {
  if (status === "failed" || status === "over_cap") return 100;
  const idx = STATUS_ORDER.indexOf(status);
  if (idx < 0) return 0;
  return Math.round(((idx + 1) / STATUS_ORDER.length) * 100);
}

// Photo import (Slice D): the pipeline reads page images instead of fetching a
// URL, so the extracting phase reads "Reading pages"; there is no fetch phase.
function statusLabelFor(job: ImportJobApiItem): string {
  if (job.kind === "photo" && job.status === "extracting") return "Reading pages";
  return STATUS_LABEL[job.status];
}

/**
 * Compact "link unfurl" preview built from the worker-parsed Open Graph
 * metadata. Every field is independently optional: the thumbnail shows only when
 * an imageKey is present and the image loads (else a CoverPlaceholder), and each
 * text line renders only when present. All text is rendered as plain text.
 * Returns null when there is nothing to show (current card behavior unchanged).
 */
function SourcePreview({
  sourceMeta,
}: {
  sourceMeta: NonNullable<ImportJobApiItem["sourceMeta"]>;
}): React.ReactElement | null {
  const [imageBroken, setImageBroken] = useState(false);

  const { title, description, siteName, imageKey } = sourceMeta;
  const hasText = title != null || description != null || siteName != null;
  if (!hasText && imageKey == null) return null;

  const imageUrl = photosObjectUrl(imageKey);
  const showImage = imageUrl != null && !imageBroken;

  return (
    <div data-testid="source-preview" className="flex gap-3 rounded-md border p-2">
      {imageKey != null && (
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded">
          {showImage ? (
            <img
              src={imageUrl}
              alt=""
              data-testid="source-preview-image"
              className="h-full w-full object-cover"
              loading="lazy"
              onError={() => setImageBroken(true)}
            />
          ) : (
            <CoverPlaceholder
              seed={imageKey}
              title={title ?? ""}
              className="h-full w-full"
              letterClassName="text-2xl"
            />
          )}
        </div>
      )}
      <div className="min-w-0 flex-1 space-y-0.5">
        {siteName != null && (
          <p className="truncate text-xs text-muted-foreground">{siteName}</p>
        )}
        {title != null && <p className="truncate text-sm font-medium">{title}</p>}
        {description != null && (
          <p className="line-clamp-2 text-xs text-muted-foreground">{description}</p>
        )}
      </div>
    </div>
  );
}

export interface JobProgressCardProps {
  job: ImportJobApiItem;
}

export function JobProgressCard({ job }: JobProgressCardProps): React.ReactElement {
  const isFailed = job.status === "failed" || job.status === "over_cap";
  const sourceLabel =
    job.kind === "url"
      ? (job.url ?? "URL import")
      : job.kind === "photo"
        ? "Photographed recipe"
        : "Pasted text";
  const statusLabel = statusLabelFor(job);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{sourceLabel}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {job.sourceMeta && <SourcePreview sourceMeta={job.sourceMeta} />}
        <Progress
          value={progressValue(job.status)}
          aria-label={`Status: ${statusLabel}`}
        />
        <div className="flex items-center justify-between text-sm">
          <span className={isFailed ? "text-destructive" : "text-muted-foreground"}>
            {statusLabel}
          </span>
          {job.costUsdCents != null && (
            <span className="text-muted-foreground">
              ${(job.costUsdCents / 100).toFixed(2)}
            </span>
          )}
        </div>
        {job.error && (
          <p className="text-sm text-destructive">
            {/* NOT_A_RECIPE carries a user-friendly message — show it plainly, not
                prefixed with the raw error code. */}
            {job.error.code === "NOT_A_RECIPE"
              ? job.error.message
              : `${job.error.code}: ${job.error.message}`}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
